// CardPreview.jsx – alle Karten nacheinander ansehen (Fotokarte + Namenskarte)
// Darstellung wie die Galerie auf der Webseite: aktuelles Kartenpaar in der Mitte,
// die nächsten Paare warten ausgegraut links und rechts.
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { MemoCard } from './MemoCard';
import { computeColorSystem } from '../utils/colorSystem';
import { orderPersonsForCards, extraPlanFor } from '../utils/cardOrder';
import { renderExtraCardCanvas } from '../utils/extraCardRender';
import { KATEGORIEN } from '../utils/extraCards';
import { renderEditedDataUrl, splitName } from '../utils/photoEdit';
import './CardPreview.css';

const GAP = 0.1; // Abstand zwischen Foto- und Namenskarte (Anteil der Kartengrösse)

function useCardSize() {
  const calc = () => {
    const w = window.innerWidth, h = window.innerHeight;
    const narrow = w < 640;
    const pairShare = narrow ? 0.96 : 0.6;                 // Anteil der Breite für das mittlere Paar
    const byWidth = (Math.min(w, 1180) - (narrow ? 20 : 48)) * pairShare / (2 + GAP);
    const byHeight = h - (narrow ? 230 : 250);             // Kopf, Name, Punkte, Fuss
    return { size: Math.floor(Math.max(110, Math.min(400, byWidth, byHeight))), narrow };
  };
  const [s, setS] = useState(calc);
  useEffect(() => {
    const on = () => setS(calc());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return s;
}

// Fotos der sichtbaren Karten in reduzierter Auflösung (Kopierschutz) erzeugen
function usePreviewPhotos(list) {
  const [urls, setUrls] = useState({});
  const key = list.map(p => p.id + ':' + (p.photo ? p.photo.length : 0) + ':' + JSON.stringify(p.photoEdit || null)).join('|');
  useEffect(() => {
    let cancelled = false;
    list.forEach(p => {
      if (!p.photo) return;
      renderEditedDataUrl(p.photo, p.photoEdit, 520, 0.82)
        .then(u => { if (!cancelled) setUrls(prev => (prev[p.id] === u ? prev : { ...prev, [p.id]: u })); })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line
  return urls;
}

// Extrakarten (Spielideen) als Bild in Vorschaugrösse
function useExtraImages(cards) {
  const [urls, setUrls] = useState({});
  const key = cards.map(c => c.key).join('|');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const c of cards) {
        try {
          const cv = await renderExtraCardCanvas(c, 520);
          if (cancelled) return;
          const u = cv.toDataURL('image/png');
          setUrls(prev => ({ ...prev, [c.key]: u }));
        } catch { /* Karte überspringen */ }
      }
    })();
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line
  return urls;
}

// showExtras: Extrakarten und Leerpersonen am Schluss zeigen (nur Admin)
export function CardPreview({ persons, relationships, customColors, canEdit, onEditPhoto, onAddPhoto, onClose, startPersonId, showExtras = false }) {
  const colors = useMemo(() => computeColorSystem(persons, relationships, customColors || {}), [persons, relationships, customColors]);
  // Reihenfolge: zuerst die mehrfarbigen Karten, dann jede Familie (Farbe) nach Alter
  const { ordered: orderedPersons, familyColors } = useMemo(
    () => orderPersonsForCards(persons, relationships, colors), [persons, relationships, colors]);
  const plan = useMemo(
    () => (showExtras && orderedPersons.length ? extraPlanFor(orderedPersons.length, familyColors) : null),
    [showExtras, orderedPersons.length, familyColors]);
  // Alle Einträge der Vorschau: Personen (Kartenpaar), Leerpersonen (2 leere Karten), Extrakarten (1 Karte)
  const ordered = useMemo(() => {
    const items = orderedPersons.map(p => ({ kind: 'person', id: p.id, p }));
    if (plan) {
      plan.leerpersonen.forEach((color, i) => items.push({ kind: 'leer', id: `leer-${i}`, color }));
      plan.extras.forEach(card => items.push({ kind: 'extra', id: `extra-${card.key}`, card }));
      plan.fuellkarten.forEach((color, i) => items.push({ kind: 'leer', single: true, id: `fuell-${i}`, color }));
    }
    return items;
  }, [orderedPersons, plan]);
  const extraUrls = useExtraImages(plan ? plan.extras : []);
  const n = ordered.length;

  const [index, setIndex] = useState(() => Math.max(0, ordered.findIndex(p => p.id === startPersonId)));
  const akt = n ? ((index % n) + n) % n : 0;
  const item = ordered[akt];
  const { size, narrow } = useCardSize();

  // Abstand zur aktuellen Karte, im Kreis (kürzester Weg)
  const offset = useCallback((i) => {
    let d = i - akt;
    if (n > 2) { if (d > n / 2) d -= n; if (d < -n / 2) d += n; }
    return d;
  }, [akt, n]);
  const visible = useMemo(() => ordered.filter((it, i) => it.kind === 'person' && Math.abs(offset(i)) <= 2).map(it => it.p), [ordered, offset]);
  const photoUrls = usePreviewPhotos(visible);

  const go = useCallback((i) => setIndex(((i % n) + n) % n), [n]);
  const prev = useCallback(() => go(akt - 1), [go, akt]);
  const next = useCallback(() => go(akt + 1), [go, akt]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next, onClose]);

  // Wischen auf dem Handy
  const touch = useRef(null);
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) (dx < 0 ? next : prev)();
  };

  if (!item) {
    return (
      <div className="cp-overlay">
        <div className="cp-empty">
          <p>Noch keine Personen im Stammbaum.</p>
          <button className="cp-btn" onClick={onClose}>Schliessen</button>
        </div>
      </div>
    );
  }

  const label = (it) => {
    if (it.kind === 'person') { const nm = splitName(it.p); return [nm.first, nm.last].filter(Boolean).join(' ') || '(ohne Namen)'; }
    if (it.kind === 'leer') return it.single ? 'Leere Karte' : 'Leerperson (2 leere Karten)';
    const t = it.card.titel.replace('-\n', '').replace('\n', ' ');
    return it.card.kategorie !== null ? `${t} – ${KATEGORIEN[it.card.kategorie]}` : t;
  };
  const currentLabel = label(item);
  const missingPhotos = orderedPersons.filter(p => !p.photo).length;
  const pairWidth = Math.round(size * (2 + GAP));

  return (
    <div className="cp-overlay">
      <div className="cp-head">
        <div className="cp-title">
          <strong>Vorschau</strong>
          <span>{item.kind === 'person' ? `Karte ${akt + 1} von ${n}` : `Extrakarte · ${akt + 1} von ${n}`}</span>
        </div>
        <select className="cp-jump" value={item.id} onChange={e => go(ordered.findIndex(it => it.id === e.target.value))} aria-label="Karte wählen">
          {ordered.map((it, i) => (
            <option key={it.id} value={it.id}>{i + 1}. {it.kind === 'person' ? '' : '★ '}{label(it)}{it.kind === 'person' && !it.p.photo ? ' – kein Foto' : ''}</option>
          ))}
        </select>
        <button className="cp-close" onClick={onClose} aria-label="Vorschau schliessen">×</button>
      </div>

      <div className="cp-stage">
        <div className="cp-panel">
          <div className="cp-flow" style={{ height: Math.round(size * 1.12) }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {ordered.map((it, i) => {
              const d = offset(i);
              if (Math.abs(d) > 3) return null;
              const pos = Math.abs(d) <= 2 ? d : (d < 0 ? -9 : 9);
              const isCurrent = d === 0;
              let content;
              if (it.kind === 'person') {
                const p = it.p;
                const ci = colors.get(p.id);
                const handlePhotoClick = isCurrent && canEdit
                  ? () => (p.photo ? onEditPhoto(p) : onAddPhoto(p))
                  : undefined;
                content = (
                  <>
                    <MemoCard person={p} colorInfo={ci} side="front" size={size}
                      photoUrl={p.photo ? photoUrls[p.id] || null : null}
                      photoPending={!!p.photo && !photoUrls[p.id]}
                      onPhotoClick={handlePhotoClick} />
                    <MemoCard person={p} colorInfo={ci} side="back" size={size} />
                  </>
                );
              } else if (it.kind === 'leer') {
                const blank = { id: it.id, name: '', gender: null, photo: null };
                content = (
                  <>
                    <MemoCard person={blank} colorInfo={{ color: it.color }} side="front" size={size} blank />
                    {!it.single && <MemoCard person={blank} colorInfo={{ color: it.color }} side="front" size={size} blank />}
                  </>
                );
              } else {
                const url = extraUrls[it.card.key];
                content = url
                  ? <img className="cp-extra" src={url} alt={label(it)} draggable={false} style={{ width: size, height: size }} onContextMenu={e => e.preventDefault()} />
                  : <div className="cp-extra cp-extra--loading" style={{ width: size, height: size }} />;
              }
              return (
                <div
                  key={it.id}
                  className={`cp-pair${narrow ? ' cp-pair--narrow' : ''}`}
                  data-pos={pos}
                  style={{ width: pairWidth, gap: Math.round(size * GAP) }}
                  onClick={isCurrent ? undefined : () => go(i)}
                  aria-hidden={!isCurrent}
                >
                  {content}
                </div>
              );
            })}
            {!narrow && <button className="cp-arrow cp-arrow--prev" onClick={prev} aria-label="Vorherige Karte" disabled={n < 2}>‹</button>}
            {!narrow && <button className="cp-arrow cp-arrow--next" onClick={next} aria-label="Nächste Karte" disabled={n < 2}>›</button>}
          </div>
          {/* Handy: Pfeile unter den Karten, damit die Karten die ganze Breite nutzen */}
          {narrow && (
            <div className="cp-arrows-row">
              <button className="cp-arrow cp-arrow--inline" onClick={prev} aria-label="Vorherige Karte" disabled={n < 2}>‹</button>
              <div className="cp-name">{currentLabel}</div>
              <button className="cp-arrow cp-arrow--inline" onClick={next} aria-label="Nächste Karte" disabled={n < 2}>›</button>
            </div>
          )}
          {!narrow && <div className="cp-name">{currentLabel}</div>}
          {n <= 60 && (
            <div className="cp-dots">
              {ordered.map((it, i) => (
                <button key={it.id} className={`${i === akt ? 'on' : ''}${it.kind === 'person' ? '' : ' extra'}`} onClick={() => go(i)} aria-label={`Karte ${i + 1}`} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="cp-foot">
        {missingPhotos > 0
          ? <span className="cp-warn">{missingPhotos} {missingPhotos === 1 ? 'Person hat' : 'Personen haben'} noch kein Foto – gedruckt wird dann ein Zierrahmen.</span>
          : <span>Alle Personen haben ein Foto.</span>}
        {plan && <span className="cp-note">Schachtel für {plan.capacity} Personen · {plan.freiePlaetze} frei → {plan.leerpersonen.length} Leerpersonen, {plan.extras.length} Extrakarten</span>}
        <span className="cp-note">Vorschau mit Wasserzeichen · Farben und Schrift können im Druck leicht abweichen.</span>
      </div>
    </div>
  );
}
